<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" clearable hide-details />
            </template>

            <template #default>
              <v-row>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.page_type_group_id"
                    :items="pageTypeGroups"
                    :limit="5"
                    label="Page Type Group"
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>

            <template #content>
              <data-table
                :headers="headers"
                :items="pageTypes"
                :footer-props="{ 'items-per-page-options': perPageOptions }"
                :page="pagination.currentPage"
                :items-per-page="pagination.perPage"
                :route="{ name: 'page-types.manage', params: ['id'] }"
                :server-items-length="pagination.total"
                disable-sort
                @update:items-per-page="$_queryHandler_onUpdateItemsPerPage"
                @update:page="$_queryHandler_onUpdatePage"
              >
                <template #item="{ item }">
                  <td>{{ item.name }}</td>
                  <td>{{ item.pageTypeGroup.name }}</td>
                  <td>
                    <tick-or-cross :value="!!item.is_ready" />
                  </td>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <fab-create v-if="isPermittedTo('create')" type="Page Type" @click="toggleCreateModal" />
    <page-type-create-modal v-model="showCreateModal" :page-type-groups="pageTypeGroups" @cancel="toggleCreateModal" />
  </page-loader>
</template>

<script>
// Services
import { pageTypes, pageTypeGroups } from '@/services/offers'
// Helpers
import { mapMutations } from 'vuex'
// Mixins
import QueryParamsHandler from '@/mixins/query-params-handler'
import PermissionsHandler from '@/mixins/permissions-handler'
// Components
import PageTypeCreateModal from './components/create-modal'
import TickOrCross from '@/components/tick-or-cross'
import DataTable from '@/components/data-table'
import FabCreate from '@/components/fab-create'
import FilterAccordion from '@/components/filter-accordion'
import ItemLogo from '@/components/item-logo'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'

export default {
  name: 'PageTypeManagement',
  components: {
    PageTypeCreateModal,
    TickOrCross,
    DataTable,
    FabCreate,
    FilterAccordion,
    ItemLogo,
    PageLoader,
    SearchInput,
    SelectList,
  },
  mixins: [QueryParamsHandler, PermissionsHandler],
  data: () => ({
    pageTypes: [],
    headers: [
      { text: 'Name', value: 'name' },
      { text: 'Page Type Groups', value: 'pageTypeGroups' },
      { text: 'Ready', value: 'is_ready' },
    ],
    isLoading: false,
    pagination: {
      perPage: 30,
    },
    perPageOptions: [15, 30, 50, 100],
    queriesDefined: ['page_type_group_id', 'searchTerm'],
    showCreateModal: false,
    pageTypeGroups: [],
  }),
  async created() {
    await Promise.all([this.getPageTypes(), this.getPageTypeGroups()])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    async getPageTypes() {
      this.isLoading = true
      try {
        const search = this.query.searchTerm
        const { currentPage: page, perPage } = this.pagination
        const { data, pagination: { total } = {} } = await pageTypes.getAll({
          search,
          filters: this.query.params,
          paginate: { page, perPage },
        })
        this.pageTypes = data
        this.pagination.total = total
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to retrieve page types', data: { error }, duration: -1 })
      }
      this.isLoading = false
    },
    async getPageTypeGroups() {
      this.isLoading = true
      try {
        const { data } = await pageTypeGroups.getAll({ paginate: { perPage: null } })
        this.pageTypeGroups = data.map(({ name, id }) => ({ key: name, value: id }))
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to retrieve page type groups', data: { error }, duration: -1 })
      }
    },
    async handleQueryUpdate() {
      await this.getPageTypes()
    },
    toggleCreateModal() {
      this.showCreateModal = !this.showCreateModal
    },
  },
}
</script>
